import React, { useState, useEffect } from 'react';
import { countryService } from '../../services/countryService';
import { premisesTypeService } from '../../services/premisesTypeService';
import { organizationService } from '../../services/organizationService';
import SearchableSelect from '../common/SearchableSelect';

const DEFAULT_OFFSITE_RADIUS_OPTIONS = ['150', '250', '450'];

const PremisesForm = ({
    premises,
    onSubmit,
    onSubmitLogo,
    onClose,
    userRole,
    userOrgId,
    organizations = [],
    onOrganizationChange,
    offsiteRadiusLocked = false
}) => {
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(!!premises); // Loading state for form population
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [premisesTypes, setPremisesTypes] = useState([]);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [citiesPopulated, setCitiesPopulated] = useState(false); // Track if cities have been loaded for editing

    const [formData, setFormData] = useState({
        org_id: '',
        premises_name: '',
        address: '',
        city_id: '',
        premises_type_id: '',
        poc_name: '',
        phone_number: '',
        contact_email: '',
        description: '',
        weekly_off_days: [],
        // Geofence
        enable_geofence: false,
        geofence: {
            radius: 100,
            latitude: '',
            longitude: ''
        },
        // GPS coords
        gps_coordinates1: '',
        gps_coordinates2: '',
        gps_coordinates3: '',
        gps_coordinates4: '',
        default_distance: '75',
        offsite_radius_options: [...DEFAULT_OFFSITE_RADIUS_OPTIONS]
    });
    const parseOffsiteRadiusOptions = (value) => {
        const parts = (value || '').split(',').map((p) => p.trim());
        if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) {
            return [...DEFAULT_OFFSITE_RADIUS_OPTIONS];
        }
        return parts;
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const resolveLogoUrl = (path) => {
        if (!path) return null;
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        return path.startsWith('http') ? path : `${base}/${path.replace(/^\//, '')}`;
    };
    const weekDays = [
        { value: 'Sunday', label: 'Sunday' },
        { value: 'Monday', label: 'Monday' },
        { value: 'Tuesday', label: 'Tuesday' },
        { value: 'Wednesday', label: 'Wednesday' },
        { value: 'Thursday', label: 'Thursday' },
        { value: 'Friday', label: 'Friday' },
        { value: 'Saturday', label: 'Saturday' }
    ];

    // Load initial data
    useEffect(() => {
        loadCountries();

        if (premises) {
            // Editing existing premises
            populateFormFromPremises(premises);
        } else if (userRole === 'client_admin' && userOrgId) {
            // Creating new premises as client admin → auto-set org_id and load types
            setFormData(prev => ({ ...prev, org_id: userOrgId }));
            loadPremisesTypesByOrgId(userOrgId);
        }
    }, [premises, userRole, userOrgId]);

    // Load cities when org changes (but not on initial load when editing with existing city)
    useEffect(() => {
        const loadCitiesForOrgCountry = async () => {
            // Skip if cities have already been populated during initial form load (editing mode)
            if (premises && citiesPopulated && formData.org_id === premises.org_id) {
                // Cities already loaded by populateFormFromPremises, don't reload
                return;
            }

            // Skip if we're editing and haven't changed the org yet (cities will be loaded by populateFormFromPremises)
            if (premises && formData.org_id === '' && premises.city_id) {
                // Still in initial state, populateFormFromPremises will handle cities
                return;
            }

            // Determine the organization ID to use - prioritize formData.org_id when it's set
            const orgIdToUse = formData.org_id || (premises ? premises.org_id : (userRole === 'core_admin' ? null : userOrgId));

            // Check if we have an org ID to proceed
            if (!orgIdToUse) {
                setCities([]); // Clear cities if no org ID is available
                return;
            }

            setLoadingCities(true);
            setError(null); // Clear previous errors when loading starts

            try {
                // Find the selected organization object in the passed list (relevant for core_admin)
                let selectedOrg = organizations.find(org => org.id == orgIdToUse);

                // If not found in the list (e.g., for client_admin or if list is incomplete) or editing existing premises
                if (!selectedOrg) {
                    // Attempt to fetch the organization details if needed
                    // This is more likely needed when editing a premises that belongs to an org not in the initial 'organizations' prop
                    try {
                        const orgDetails = await organizationService.getOrganizationById(orgIdToUse);
                        selectedOrg = orgDetails;
                    } catch (fetchErr) {
                        console.error(`Error fetching organization details for ID ${orgIdToUse}:`, fetchErr);
                        setCities([]);
                        setError(`Failed to load organization details for ID ${orgIdToUse}. Cannot load cities.`);
                        return;
                    }
                }

                if (selectedOrg && selectedOrg.country_code) {
                    // Use the NEW service function (assumes getCitiesByCountryCode is defined in countryService)
                    const cityData = await countryService.getCitiesByCountryCode(selectedOrg.country_code);
                    setCities(cityData.cities || []);
                } else {
                    console.warn(`Organization ${orgIdToUse} does not have a country_code.`);
                    setCities([]); // No country code means no cities to load
                    setError(`Organization ${orgIdToUse} does not have a country code. Cannot load cities.`);
                }
            } catch (err) {
                console.error('Error loading cities for organization:', err);
                setCities([]); // Ensure empty array on error
                // Set a more specific error message
                setError('Failed to load cities for the selected organization. Please check the organization details.');
            } finally {
                setLoadingCities(false);
            }
        };

        loadCitiesForOrgCountry();
    }, [formData.org_id, userOrgId, userRole, premises?.org_id, organizations]); // Watch formData.org_id and premises.org_id

    const populateFormFromPremises = async (premises) => {
        try {
            setInitialLoading(true);

            // Load premises types first before setting form data
            if (premises.org_id) {
                await loadPremisesTypesByOrgId(premises.org_id);
            }

            // Load cities via org country_code (single fast API call, no N+1 loop)
            if (premises.org_id) {
                try {
                    let org = organizations.find(o => o.id == premises.org_id);
                    if (!org) {
                        org = await organizationService.getOrganizationById(premises.org_id);
                    }
                    if (org && org.country_code) {
                        const cityData = await countryService.getCitiesByCountryCode(org.country_code);
                        setCities(cityData.cities || []);
                        setCitiesPopulated(true); // Prevent useEffect from reloading cities
                    }
                } catch (err) {
                    console.error('Error loading cities for premises org:', err);
                }
            }

            // Ensure current city is present in dropdown even if not returned by country lookup
            if (premises.city_id && !cities.some(c => c.id == premises.city_id)) {
                setCities(prev => [...prev, { id: premises.city_id, name: premises.city_name || 'Selected City' }]);
            }

            // Set form data after premises types and cities are loaded
            setFormData({
            org_id: premises.org_id ? premises.org_id.toString() : '',
            premises_name: premises.premises_name || '',
            address: premises.address?.replace(/^"(.*)"$/, '$1') || '',
            city_id: premises.city_id ? premises.city_id.toString() : '',
            premises_type_id: premises.premises_type_id ? premises.premises_type_id.toString() : '',
            poc_name: premises.poc_name || '',
            phone_number: premises.phone_number || '',
            contact_email: premises.contact_email || '',
            description: premises.description?.replace(/^"(.*)"$/, '$1') || '',
            weekly_off_days: premises.weekly_off_days
                ? premises.weekly_off_days.split(',')
                : [],
            enable_geofence: !!(premises.center_latitude || premises.radius),
            geofence: {
                radius: premises.radius ? parseInt(premises.radius) : 100,
                latitude: premises.center_latitude || '',
                longitude: premises.center_longitude || ''
            },
            gps_coordinates1: premises.gps_cordinates1?.replace(/^"(.*)"$/, '$1') || '',
            gps_coordinates2: premises.gps_cordinates2?.replace(/^"(.*)"$/, '$1') || '',
            gps_coordinates3: premises.gps_cordinates3?.replace(/^"(.*)"$/, '$1') || '',
            gps_coordinates4: premises.gps_cordinates4?.replace(/^"(.*)"$/, '$1') || '',
            default_distance: premises.default_distance || '75',
            offsite_radius_options: parseOffsiteRadiusOptions(premises.offsite_radius_options)
        });
            setLogoPreview(resolveLogoUrl(premises.logo));
        } catch (err) {
            console.error('Error populating form:', err);
            setError('Failed to load premises data');
        } finally {
            setInitialLoading(false);
        }
    };

    const loadCountries = async () => {
        try {
            const data = await countryService.getCountries();
            setCountries(data.countries || []);
        } catch (err) {
            console.error('Error loading countries:', err);
            setCountries([]);
        }
    };

    const loadCitiesForCountry = async (countryId) => {
        if (!countryId) {
            setCities([]);
            return;
        }
        setLoadingCities(true);
        try {
            const data = await countryService.getCities(countryId);
            setCities(data.cities || []);
        } catch (err) {
            console.error('Error loading cities:', err);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    };

    // Helper: Given a city_id, find its country and load cities
    const loadCountryAndCitiesForCity = async (cityId) => {
        try {
            const countriesData = await countryService.getCountries();
            for (const country of countriesData.countries || []) {
                const citiesData = await countryService.getCities(country.id);
                const city = citiesData.cities?.find(c => c.id == cityId);
                if (city) {
                    setSelectedCountryId(country.id);
                    setCities(citiesData.cities || []);
                    return;
                }
            }
        } catch (err) {
            console.error('Error resolving country for city:', err);
        }
    };

    const loadPremisesTypesByOrgId = async (orgId) => {
        if (!orgId) {
            setPremisesTypes([]);
            return;
        }

        try {
            // Get org category
            let orgCategoryId = null;
            if (userRole === 'core_admin') {
                const org = organizations.find(o => o.id == orgId);
                orgCategoryId = org?.org_category_id;
            } else {
                // For non-core_admin, we assume org_id matches current user org
                // You may need to adjust based on your auth context
                const orgResponse = await organizationService.getOrganizationById(orgId);
                orgCategoryId = orgResponse.org_category_id;
            }

            if (orgCategoryId) {
                const types = await premisesTypeService.getPremisesTypesByOrgCategory(orgCategoryId);
                setPremisesTypes(types.premises_types || []);
            } else {
                setPremisesTypes([]);
            }
        } catch (err) {
            console.error('Error loading premises types:', err);
            setPremisesTypes([]);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (field === 'org_id' && value !== formData.org_id) {
            setFormData((prev) => ({ ...prev, premises_type_id: '', city_id: '', org_id: value }));
            loadPremisesTypesByOrgId(value);
            setCitiesPopulated(false); // Allow cities to be reloaded for new org
            if (onOrganizationChange) onOrganizationChange(value);
        }
    };

    const handleRadiusOptionChange = (index, value) => {
        setFormData((prev) => {
            const offsite_radius_options = [...prev.offsite_radius_options];
            offsite_radius_options[index] = value;
            return { ...prev, offsite_radius_options };
        });
    };

    const handleGeofenceChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            geofence: { ...prev.geofence, [field]: value }
        }));
    };

    const handleWeeklyOffDayChange = (day, checked) => {
        setFormData((prev) => ({
            ...prev,
            weekly_off_days: checked
                ? [...prev.weekly_off_days, day]
                : prev.weekly_off_days.filter((d) => d !== day)
        }));
    };

    const handleCountryChange = (countryId) => {
        setSelectedCountryId(countryId);
        setFormData((prev) => ({ ...prev, city_id: '' }));
        if (countryId) {
            loadCitiesForCountry(countryId);
        } else {
            setCities([]);
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    handleGeofenceChange('latitude', position.coords.latitude.toString());
                    handleGeofenceChange('longitude', position.coords.longitude.toString());
                },
                (error) => {
                    setError('Unable to get current location: ' + error.message);
                }
            );
        } else {
            setError('Geolocation is not supported by this browser');
        }
    };

    const validateForm = () => {
        const errors = [];
        if (userRole === 'core_admin' && !formData.org_id) {
            errors.push('Organization is required');
        }
        if (!formData.premises_name.trim()) errors.push('Premises name is required');
        if (!formData.address.trim()) errors.push('Address is required');
        if (!formData.premises_type_id) errors.push('Premises type is required');
        if (formData.enable_geofence) {
            if (!formData.geofence.latitude || !formData.geofence.longitude) {
                errors.push('Latitude and longitude are required for geofencing');
            }
            if (formData.geofence.radius < 10 || formData.geofence.radius > 5000) {
                errors.push('Geofence radius must be between 10 and 5000 meters');
            }
        }
        if (
            formData.contact_email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)
        ) {
            errors.push('Please enter a valid email address');
        }
        if (formData.default_distance && !/^\d+(\.\d+)?$/.test(formData.default_distance.trim())) {
            errors.push('Default distance must be a number, e.g. 75');
        } else if (formData.default_distance && parseFloat(formData.default_distance) > 500) {
            errors.push('Default distance cannot exceed 500 meters');
        }
        if (!offsiteRadiusLocked || userRole === 'core_admin') {
            const radiusValues = formData.offsite_radius_options;
            if (radiusValues.length !== 3 || radiusValues.some((v) => {
                const n = parseInt(v, 10);
                return !/^\d+$/.test(String(v).trim()) || n < 10 || n > 500;
            })) {
                errors.push('Off-site radius options must be 3 whole numbers between 10 and 500 meters');
            }
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(', '));
            return;
        }

        setLoading(true);
        setError(null);
        try {
            let submitData;

            if (premises) {
                // Editing existing premises - only send changed fields + logo
                const changedFields = {};

                // Check each field for changes
                if (formData.premises_name !== premises.premises_name)
                    changedFields.premises_name = formData.premises_name.trim();
                if (formData.address !== premises.address?.replace(/^"(.*)"$/, '$1'))
                    changedFields.address = formData.address.trim();
                if (formData.org_id !== premises.org_id)
                    changedFields.org_id = formData.org_id || '';
                if (formData.premises_type_id && formData.premises_type_id !== premises.premises_type_id)
                    changedFields.premises_type_id = formData.premises_type_id;
                if (formData.city_id && formData.city_id !== premises.city_id)
                    changedFields.city_id = formData.city_id;
                if (formData.poc_name !== premises.poc_name)
                    changedFields.poc_name = formData.poc_name.trim() || '';
                if (formData.phone_number !== premises.phone_number)
                    changedFields.phone_number = formData.phone_number.trim() || '';
                if (formData.contact_email !== premises.contact_email)
                    changedFields.contact_email = formData.contact_email.trim() || '';
                if (formData.description !== premises.description?.replace(/^"(.*)"$/, '$1'))
                    changedFields.description = formData.description.trim() || '';
                if (formData.weekly_off_days.join(',') !== (premises.weekly_off_days || ''))
                    changedFields.weekly_off_days = formData.weekly_off_days.join(',');
                if (formData.gps_coordinates1 !== premises.gps_cordinates1?.replace(/^"(.*)"$/, '$1'))
                    changedFields.gps_coordinates1 = formData.gps_coordinates1 || '';
                if (formData.gps_coordinates2 !== premises.gps_cordinates2?.replace(/^"(.*)"$/, '$1'))
                    changedFields.gps_coordinates2 = formData.gps_coordinates2 || '';
                if (formData.gps_coordinates3 !== premises.gps_cordinates3?.replace(/^"(.*)"$/, '$1'))
                    changedFields.gps_coordinates3 = formData.gps_coordinates3 || '';
                if (formData.gps_coordinates4 !== premises.gps_cordinates4?.replace(/^"(.*)"$/, '$1'))
                    changedFields.gps_coordinates4 = formData.gps_coordinates4 || '';
                if ((formData.default_distance || '75') !== (premises.default_distance || '75'))
                    changedFields.default_distance = (formData.default_distance || '75').trim();
                if (!offsiteRadiusLocked || userRole === 'core_admin') {
                    const newRadiusOptions = formData.offsite_radius_options.join(',');
                    if (newRadiusOptions !== (premises.offsite_radius_options || DEFAULT_OFFSITE_RADIUS_OPTIONS.join(',')))
                        changedFields.offsite_radius_options = newRadiusOptions;
                }

                // Handle geofence
                if (formData.enable_geofence) {
                    changedFields.geofence = {
                        radius: parseInt(formData.geofence.radius),
                        latitude: parseFloat(formData.geofence.latitude),
                        longitude: parseFloat(formData.geofence.longitude)
                    };
                } else if (premises.center_latitude || premises.radius) {
                    // Explicitly clear geofence if disabled
                    changedFields.geofence = { radius: 0, latitude: '', longitude: '' };
                }

                // Use FormData only if logo is being updated
                if (logoFile || (premises.logo && !logoFile)) {
                    submitData = new FormData();

                    // Append changed fields
                    Object.entries(changedFields).forEach(([key, value]) => {
                        if (key === 'geofence' && value) {
                            submitData.append('geofence[radius]', value.radius);
                            submitData.append('geofence[latitude]', value.latitude);
                            submitData.append('geofence[longitude]', value.longitude);
                        } else if (value !== undefined && value !== null) {
                            submitData.append(key, value);
                        }
                    });

                    // Handle logo
                    if (logoFile) {
                        submitData.append('logo', logoFile);
                    } else if (!logoFile && premises.logo) {
                        // Explicitly remove logo
                        submitData.append('logo', '');
                    }
                } else {
                    // No logo change - send as JSON
                    submitData = changedFields;
                }
            } else {
                // Creating new premises - send all fields
                if (logoFile) {
                    submitData = new FormData();
                    Object.entries({
                        org_id: formData.org_id || '',
                        premises_type_id: formData.premises_type_id,
                        premises_name: formData.premises_name.trim(),
                        address: formData.address.trim(),
                        city_id: formData.city_id || '',
                        poc_name: formData.poc_name.trim(),
                        phone_number: formData.phone_number.trim(),
                        contact_email: formData.contact_email.trim(),
                        description: formData.description.trim(),
                        weekly_off_days: formData.weekly_off_days.join(','),
                        gps_coordinates1: formData.gps_coordinates1 || '',
                        gps_coordinates2: formData.gps_coordinates2 || '',
                        gps_coordinates3: formData.gps_coordinates3 || '',
                        gps_coordinates4: formData.gps_coordinates4 || '',
                        default_distance: (formData.default_distance || '75').trim(),
                        offsite_radius_options: formData.offsite_radius_options.join(',')
                    }).forEach(([key, value]) => {
                        if (value !== null && value !== undefined && value !== '') {
                            submitData.append(key, value);
                        }
                    });

                    if (formData.enable_geofence) {
                        submitData.append('geofence[radius]', formData.geofence.radius);
                        submitData.append('geofence[latitude]', formData.geofence.latitude);
                        submitData.append('geofence[longitude]', formData.geofence.longitude);
                    }

                    if (logoFile) {
                        submitData.append('logo', logoFile);
                    }
                } else {
                    submitData = {
                        org_id: formData.org_id || undefined,
                        premises_type_id: formData.premises_type_id,
                        premises_name: formData.premises_name.trim(),
                        address: formData.address.trim(),
                        city_id: formData.city_id || null,
                        poc_name: formData.poc_name.trim() || null,
                        phone_number: formData.phone_number.trim() || null,
                        contact_email: formData.contact_email.trim() || null,
                        description: formData.description.trim() || null,
                        weekly_off_days: formData.weekly_off_days,
                        gps_coordinates1: formData.gps_coordinates1 || null,
                        gps_coordinates2: formData.gps_coordinates2 || null,
                        gps_coordinates3: formData.gps_coordinates3 || null,
                        gps_coordinates4: formData.gps_coordinates4 || null,
                        default_distance: (formData.default_distance || '75').trim(),
                        offsite_radius_options: formData.offsite_radius_options.join(',')
                    };

                    if (formData.enable_geofence) {
                        submitData.geofence = {
                            radius: parseInt(formData.geofence.radius),
                            latitude: parseFloat(formData.geofence.latitude),
                            longitude: parseFloat(formData.geofence.longitude)
                        };
                    }
                }
            }

            await onSubmit(submitData, logoFile ? true : false);
        } catch (err) {
            setError(err.message || 'Failed to save premises');
        } finally {
            setLoading(false);
        }
    };
    const effectiveOrgId = premises ? premises.org_id : (userRole === 'core_admin' ? formData.org_id : userOrgId);
    const cityOptions = cities.map((city) => ({
        id: city.id?.toString(),
        name: city.name
    }));
    const organizationOptions = organizations.map((org) => ({
        id: org.id?.toString(),
        name: org.name
    }));

    return (

        <form onSubmit={handleSubmit} className="modal-body">
            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            )}

            {initialLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner">
                        <i className="fas fa-spinner fa-spin"></i>
                    </div>
                    <p>Loading premises data...</p>
                </div>
            ) : (
            <div className="form-sections">
                {/* Basic Information */}
                <div className="form-section">
                    <h4>
                        <i className="fas fa-building"></i>
                        Basic Information
                    </h4>
                    <div className="form-grid">
                        {userRole === 'core_admin' && (
                            <div className="form-group full-width">
                                <label>Organization *</label>
                                <SearchableSelect
                                    options={organizationOptions}
                                    value={formData.org_id}
                                    onChange={(value) => handleInputChange('org_id', value || '')}
                                    placeholder="Select Organization"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search organizations..."
                                    className="form-input"
                                    required
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <label>Premises Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.premises_name}
                                onChange={(e) => handleInputChange('premises_name', e.target.value)}
                                placeholder="Enter premises name"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Premises Type *</label>
                            <select
                                className="form-input"
                                value={formData.premises_type_id}
                                onChange={(e) => handleInputChange('premises_type_id', e.target.value)}
                                required
                                disabled={!formData.org_id && userRole === 'core_admin'}
                            >
                                <option value="">Select Premises Type</option>
                                {premisesTypes.map((type) => (
                                    <option key={type.id} value={type.id?.toString()}>
                                        {type.premisese_type_name}
                                    </option>
                                ))}
                            </select>
                            {userRole === 'core_admin' && !formData.org_id && (
                                <small className="form-help">Please select an organization first</small>
                            )}
                        </div>
                        <div className="form-group full-width">
                            <label>Address *</label>
                            <textarea
                                className="form-input"
                                value={formData.address}
                                onChange={(e) => handleInputChange('address', e.target.value)}
                                placeholder="Enter complete address"
                                rows="3"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>City *</label> {/* Make City required if needed */}
                            <SearchableSelect
                                options={cityOptions}
                                value={formData.city_id}
                                onChange={(value) => handleInputChange('city_id', value)}
                                placeholder={
                                    loadingCities
                                        ? 'Loading cities...'
                                        : !effectiveOrgId
                                            ? 'Organization not selected'
                                            : 'Select City'
                                }
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search cities..."
                                className="form-input"
                                disabled={loadingCities || !effectiveOrgId}
                                required
                                loading={loadingCities}
                            />
                            {!effectiveOrgId && ( // Use effectiveOrgId
                                <small className="form-help">Organization must be selected first.</small>
                            )}
                            {effectiveOrgId && !loadingCities && cities.length === 0 && ( // Use effectiveOrgId
                                <small className="form-help">No cities available for this organization's country.</small>
                            )}
                        </div>
                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea
                                className="form-input"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="Enter premises description"
                                rows="3"
                            />
                        </div>
                    </div>
                </div>
                <div className="form-group full-width">
                    <label>Premises Logo</label>
                    <div className="logo-upload">
                        {logoPreview && (
                            <img src={logoPreview} alt="Logo preview" className="logo-preview" />
                        )}
                        <div className="logo-actions">
                            <label className="file-upload-btn">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setLogoFile(file);
                                            setLogoPreview(URL.createObjectURL(file));
                                        }
                                    }}
                                />
                                {logoFile ? 'Change Logo' : 'Upload Logo'}
                            </label>

                            {/* Save Logo Button (only when editing) */}
                            {premises && logoFile && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary save-logo-btn"
                                    onClick={async () => {
                                        try {
                                            setLoading(true);
                                            await onSubmitLogo(logoFile); // New handler
                                            showToast('Logo updated successfully!', 'success');
                                        } catch (err) {
                                            setError(err.message);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save Logo'}
                                </button>
                            )}

                            {/* Remove Logo Button */}
                            {premises && (logoFile || premises.logo) && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary remove-logo-btn"
                                    onClick={async () => {
                                        if (window.confirm('Are you sure you want to remove the logo?')) {
                                            try {
                                                setLoading(true);
                                                await onSubmitLogo(null, true); // remove = true
                                                setLogoFile(null);
                                                setLogoPreview(null);
                                                showToast('Logo removed successfully!', 'success');
                                            } catch (err) {
                                                setError(err.message);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                >
                                    Remove Logo
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="form-help">Recommended: PNG or JPG, max 2MB</p>
                </div>
                {/* Contact Information */}
                <div className="form-section">
                    <h4>
                        <i className="fas fa-address-book"></i>
                        Contact Information
                    </h4>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Point of Contact</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.poc_name}
                                onChange={(e) => handleInputChange('poc_name', e.target.value)}
                                placeholder="Enter contact person name"
                            />
                        </div>
                        <div className="form-group">
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                className="form-input"
                                value={formData.phone_number}
                                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                                placeholder="Enter phone number"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Contact Email</label>
                            <input
                                type="email"
                                className="form-input"
                                value={formData.contact_email}
                                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                                placeholder="Enter email address"
                            />
                        </div>
                    </div>
                </div>

                {/* Working Days */}
                <div className="form-section">
                    <h4>
                        <i className="fas fa-calendar"></i>
                        Working Schedule
                    </h4>
                    <div className="form-group">
                        <label>Weekly Off Days</label>
                        <div className="weekdays-grid">
                            {weekDays.map((day) => (
                                <label key={day.value} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.weekly_off_days.includes(day.value)}
                                        onChange={(e) => handleWeeklyOffDayChange(day.value, e.target.checked)}
                                    />
                                    {day.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* GPS Coordinates (Advanced) */}
                <div className="form-section">
                    <h4>
                        <i className="fas fa-map"></i>
                        GPS Coordinates (Optional)
                    </h4>
                    <p className="section-description">
                        Define specific GPS boundary points for complex premises shapes
                    </p>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>GPS Point 1</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.gps_coordinates1}
                                onChange={(e) => handleInputChange('gps_coordinates1', e.target.value)}
                                placeholder="lat,lng"
                            />
                        </div>
                        <div className="form-group">
                            <label>GPS Point 2</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.gps_coordinates2}
                                onChange={(e) => handleInputChange('gps_coordinates2', e.target.value)}
                                placeholder="lat,lng"
                            />
                        </div>
                        <div className="form-group">
                            <label>GPS Point 3</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.gps_coordinates3}
                                onChange={(e) => handleInputChange('gps_coordinates3', e.target.value)}
                                placeholder="lat,lng"
                            />
                        </div>
                        <div className="form-group">
                            <label>GPS Point 4</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.gps_coordinates4}
                                onChange={(e) => handleInputChange('gps_coordinates4', e.target.value)}
                                placeholder="lat,lng"
                            />
                        </div>
                        <div className="form-group">
                            <label>Default Distance</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.default_distance}
                                onChange={(e) => handleInputChange('default_distance', e.target.value)}
                                placeholder="75"
                            />
                        </div>
                    </div>
                </div>

                {/* Off-site Attendance Radius Options */}
                <div className="form-section">
                    <h4>
                        <i className="fas fa-map-marker-alt"></i>
                        Off-site Attendance Radius Options
                    </h4>
                    <p className="section-description">
                        Define the 3 radius choices (in meters, 10-500) staff can pick from when marking off-site attendance
                    </p>
                    <div className="form-grid">
                        {[0, 1, 2].map((index) => (
                            <div className="form-group" key={index}>
                                <label>Radius Option {index + 1} (meters)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    min="10"
                                    max="500"
                                    value={formData.offsite_radius_options[index]}
                                    onChange={(e) => handleRadiusOptionChange(index, e.target.value)}
                                    disabled={offsiteRadiusLocked && userRole !== 'core_admin'}
                                    placeholder="e.g. 150"
                                />
                            </div>
                        ))}
                    </div>
                    {offsiteRadiusLocked && userRole !== 'core_admin' && (
                        <p className="form-help" style={{ padding: '0 var(--spacing-lg) var(--spacing-lg)' }}>
                            Locked by your organization administrator.
                        </p>
                    )}
                </div>
            </div>
            )}

            <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading || initialLoading}>
                    {loading ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i>
                            {premises ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save"></i>
                            {premises ? 'Update Premises' : 'Create Premises'}
                        </>
                    )}
                </button>
            </div>




            <style jsx>{`
                /* All existing styles remain the same */             


                
                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                    border-radius: var(--radius-sm);
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    color: var(--text-dark);
                    background: rgba(0, 0, 0, 0.05);
                }

              

                .error-message {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                }

                .form-sections {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xl);
                }

                .form-section {
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    border-radius: var(--radius-lg);
                    overflow: hidden;
                }

                .form-section h4 {
                    background: var(--light-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: var(--spacing-lg);
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .form-section h4 i {
                    color: var(--admin-primary);
                }

                .section-description {
                    color: var(--text-light);
                    font-size: 0.85rem;
                    margin: 0;
                    padding: var(--spacing-md) var(--spacing-lg) 0;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: var(--spacing-lg);
                    padding: var(--spacing-lg);
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-sm);
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                    background: var(--white);
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .form-input:disabled {
                    background: var(--light-color);
                    color: var(--text-light);
                    cursor: not-allowed;
                }

                .form-help {
                    font-size: 0.8rem;
                    color: var(--text-light);
                    margin: var(--spacing-xs) 0 0 0;
                    line-height: 1.4;
                }

                .weekdays-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: var(--spacing-sm);
                    padding: var(--spacing-md);
                    background: var(--light-color);
                    border-radius: var(--radius-md);
                    margin-top: var(--spacing-sm);
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    font-size: 0.85rem;
                    cursor: pointer;
                    padding: var(--spacing-xs);
                    border-radius: var(--radius-sm);
                    transition: background-color 0.2s ease;
                }

                .checkbox-label:hover {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .checkbox-label input[type="checkbox"] {
                    accent-color: var(--admin-primary);
                    margin: 0;
                }

                .geofence-settings {
                    background: var(--light-color);
                    border-radius: var(--radius-md);
                    padding: var(--spacing-lg);
                    margin-top: var(--spacing-md);
                }

                .location-helper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-sm);
                    margin: var(--spacing-lg) 0;
                    padding: var(--spacing-lg);
                    background: var(--white);
                    border-radius: var(--radius-md);
                    border: 1px solid rgba(0, 0, 0, 0.05);
                }

                .geofence-preview {
                    background: var(--white);
                    border-radius: var(--radius-md);
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    margin-top: var(--spacing-lg);
                    overflow: hidden;
                }

                .preview-header {
                    background: var(--admin-primary);
                    color: white;
                    padding: var(--spacing-md) var(--spacing-lg);
                }

                .preview-header h5 {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                }

                .preview-content {
                    padding: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xl);
                }

                .geofence-circle {
                    position: relative;
                    width: 100px;
                    height: 100px;
                    border: 2px dashed var(--admin-primary);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .center-point {
                    width: 6px;
                    height: 6px;
                    background: var(--admin-primary);
                    border-radius: 50%;
                }

                .radius-text {
                    position: absolute;
                    top: -20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: var(--admin-primary);
                    color: white;
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.7rem;
                    font-weight: 600;
                }

                .coordinates-info {
                    flex: 1;
                }

                .coordinates-info p {
                    margin: 0 0 var(--spacing-xs) 0;
                    font-size: 0.85rem;
                    color: var(--text-dark);
                }

             
                .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    min-width: 120px;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: var(--admin-secondary);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: var(--white);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f8f9fa;
                    border-color: var(--admin-primary);
                }

                .fa-spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Responsive Design */
               
                .logo-upload {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}
.logo-preview {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: var(--radius-md);
  border: 1px solid rgba(0,0,0,0.1);
}
.file-upload-btn {
  display: inline-block;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--light-color);
  border: 1px dashed #ccc;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}
.file-upload-btn:hover {
  background: #f0f0f0;
}
.file-upload-btn input {
  display: none;
}
.remove-logo-btn {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 0.8rem;
}

.logo-actions {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  align-items: center;
}
.save-logo-btn, .remove-logo-btn {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 0.8rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 400px;
}

.loading-spinner {
  font-size: 3rem;
  color: var(--admin-primary);
  margin-bottom: 1rem;
}

.loading-spinner i {
  animation: spin 1s linear infinite;
}

.loading-container p {
  color: var(--text-light);
  font-size: 1rem;
  margin: 0;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
            `}</style>
        </form>
    );
};

export default PremisesForm;
